import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import instance from "../instance";
import CustomerSearch from "./Customers/CustomerSearch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

const TextsManagement = () => {
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editPrice, setEditPrice] = useState(false);
  const [newText, setNewText] = useState("");
  const [newPrice, setNewPrice] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showPriceOnly, setShowPriceOnly] = useState(false);
  const [showIsTrueOnly, setShowIsTrueOnly] = useState(false);
  const [selectAllPrice, setSelectAllPrice] = useState(false);
  const [selectAllIsTrue, setSelectAllIsTrue] = useState(false);
  const [hasShownSelectAllNotification, setHasShownSelectAllNotification] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    let timer;
    if (transferSuccess) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            window.location.reload();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [transferSuccess]);

  const navigate = useNavigate();
  
  const fetchTexts = async () => {
    try {
      const response = await instance.get("texts/all");
      setTexts(response.data.data);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTexts();
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExcludeSearch = (event) => {
    setExcludeTerm(event.target.value);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
  };

  const handleEdit = (text) => {
    setEditingId(text.ID);
    setEditText(text.Text);
    setEditPrice(text.Price);
  };

  const handleUpdate = async (id, updatedData) => {
    try {
      await instance.put(`texts/${id}`, updatedData);
      toast.success("Text updated successfully");
      if (editingId === id) {
        setEditingId(null);
      }
      fetchTexts();
    } catch (error) {
      toast.error("Error updating text: " + error.message);
    }
  };

  const handleDelete = async (id) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this text?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              await instance.delete(`texts/${id}`);
              toast.success("Text deleted successfully");
              fetchTexts();
            } catch (error) {
              toast.error("Error deleting text: " + error.message);
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleDeleteAllVisible = async () => {
    confirmAlert({
      title: "Confirm Deletion",
      message: `Are you sure you want to delete all ${filteredTexts.length} visible items?`,
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            setIsDeleting(true);
            try {
              let deletedCount = 0;
              for (const text of filteredTexts) {
                try {
                  await instance.delete(`texts/${text.ID}`);
                  deletedCount++;
                } catch (error) {
                  console.error(`Error deleting text ID ${text.ID}:`, error);
                }
              }
              
              toast.success(`Successfully deleted ${deletedCount} items`);
              fetchTexts();
            } catch (error) {
              toast.error("Error during bulk deletion: " + error.message);
            } finally {
              setIsDeleting(false);
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleAdd = async () => {
    if (!newText.trim()) {
      toast.warning("Please enter a text");
      return;
    }
    try {
      await instance.post("texts", {
        text: newText,
        price: newPrice,
      });
      toast.success("Text added successfully");
      setNewText("");
      setNewPrice(false);
      fetchTexts();
    } catch (error) {
      toast.error("Error adding text: " + error.message);
    }
  };

  const handleToggleIsTrue = async (text) => {
    await handleUpdate(text.ID, {
      text: text.Text,
      isTrue: !text.IsTrue,
      price: text.Price,
    });
  };

  const handleTogglePrice = async (text) => {
    await handleUpdate(text.ID, {
      text: text.Text,
      isTrue: text.IsTrue,
      price: !text.Price,
    });
  };

  const handleSelectAllPrice = async () => {
    const newValue = !selectAllPrice;
    setSelectAllPrice(newValue);

    if (newValue && !hasShownSelectAllNotification) {
      toast.info("Updating all visible items...");
      setHasShownSelectAllNotification(true);
    }

    for (const text of filteredTexts) {
      if (text.Price !== newValue) {
        await handleUpdate(text.ID, {
          text: text.Text,
          isTrue: text.IsTrue,
          price: newValue,
        });
      }
    }
  };

  const handleSelectAllIsTrue = async () => {
    const newValue = !selectAllIsTrue;
    setSelectAllIsTrue(newValue);

    if (newValue && !hasShownSelectAllNotification) {
      toast.info("Updating all visible items...");
      setHasShownSelectAllNotification(true);
    }

    for (const text of filteredTexts) {
      if (text.IsTrue !== newValue) {
        await handleUpdate(text.ID, {
          text: text.Text,
          isTrue: newValue,
          price: text.Price,
        });
      }
    }
  };

  const getAllUniqueValues = () => {
    const uniqueValues = new Set();
    texts.forEach((item) => {
      uniqueValues.add(item.Text);
      uniqueValues.add(item.IsTrue ? "True" : "False");
      uniqueValues.add(item.Price ? "Price True" : "Price False");
    });
    return Array.from(uniqueValues).sort();
  };

  const filteredTexts = texts.filter((text) => {
    const searchTermLower = searchTerm.toLowerCase();
    const excludeTermLower = excludeTerm.toLowerCase();
    const textValue = text.Text.toLowerCase();
    const isTrueValue = text.IsTrue ? "true" : "false";
    const isPriceValue = text.Price ? "true" : "false";

    const matchesSearch =
      searchTerm === "" ||
      textValue.includes(searchTermLower) ||
      isTrueValue.includes(searchTermLower) ||
      isPriceValue.includes(searchTermLower);

    const matchesExclude =
      excludeTerm === "" ||
      !(
        textValue.includes(excludeTermLower) ||
        isTrueValue.includes(excludeTermLower) ||
        isPriceValue.includes(excludeTermLower)
      );

    const matchesPriceFilter = !showPriceOnly || text.Price;
    const matchesIsTrueFilter = !showIsTrueOnly || text.IsTrue;

    return (
      matchesSearch &&
      matchesExclude &&
      matchesPriceFilter &&
      matchesIsTrueFilter
    );
  });

  // Format date function for LastUpdated column
  const formatDate = (dateString) => {
    if (!dateString) return "-";
  
    const date = new Date(dateString);
  
    // استخراج مكونات التاريخ والوقت بدون تغيير التوقيت
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    let hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  
    // تحويل الساعة إلى نظام 12 ساعة
    const amPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // تحويل 0 إلى 12 في نظام 12 ساعة
  
    return `${year}/${month}/${day} , ${hours}:${minutes}:${seconds} ${amPm}`;
  };
  
  
  


  // تعديل دالة handleSort لتتعامل مع التواريخ بشكل صحيح
const handleSort = (key) => {
  let direction = "asc";
  if (sortConfig.key === key && sortConfig.direction === "asc") {
    direction = "desc";
  }
  setSortConfig({ key, direction });

  const sortedTexts = [...texts].sort((a, b) => {
    // معالجة خاصة لعمود التاريخ
    if (key === "LastUpdated") {
      const dateA = a[key] ? new Date(a[key]) : new Date(0);
      const dateB = b[key] ? new Date(b[key]) : new Date(0);
      return direction === "asc" 
        ? dateA.getTime() - dateB.getTime() 
        : dateB.getTime() - dateA.getTime();
    }
    
    // للأعمدة الأخرى
    if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
    if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
    return 0;
  });
  
  setTexts(sortedTexts);
};

  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <header>
        <h1>Texts Management</h1>
      </header>

      <div style={{ textAlign: "center" }}>
        <button onClick={() => navigate("/")} className="button">
          ← Main
        </button>
  
        <button
          onClick={async () => {
            setIsLoading(true);
            try {
              await instance.post("texts/transfer-prices");
              toast.success("Price transfer has been started successfully!");
              setTransferSuccess(true);
            } catch (error) {
              toast.error("An error occurred while transferring prices: " + error.message);
            } finally {
              setIsLoading(false);
            }
          }}
          className="button"
          style={{ 
            margin: "10px", 
            padding: "10px", 
            fontSize: "16px",
            backgroundColor: isLoading ? "#ccc" : undefined 
          }}
          disabled={isLoading || transferSuccess}
        >
          {isLoading ? "Data transferring..." : 
           transferSuccess ? ` Refresh ${countdown} second` : 
           "Transfer prices"}
        </button>
      </div>

      <CustomerSearch
        searchTerm={searchTerm}
        excludeTerm={excludeTerm}
        datalistData={getAllUniqueValues()}
        handleSearch={handleSearch}
        handleExcludeSearch={handleExcludeSearch}
        resetSearch={resetSearch}
        dateFilter={null}
        formatDateRange={() => ""}
      />

      <div
        style={{
          margin: "20px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Enter new text..."
          style={{
            flex: 1,
            padding: "8px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label style={{ fontSize: "16px", fontWeight: "bold" }}>
            Price:
            <input
              type="checkbox"
              checked={newPrice}
              onChange={(e) => setNewPrice(e.target.checked)}
              style={{
                marginLeft: "5px",
                width: "20px",
                height: "20px",
                cursor: "pointer",
              }}
            />
          </label>
        </div>
        <button onClick={handleAdd} className="button">
          Add Text
        </button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          margin: "20px 0",
        }}
      >
        <button
          onClick={() => setShowIsTrueOnly(!showIsTrueOnly)}
          className={`button ${showIsTrueOnly ? "active" : ""}`}
          style={{
            backgroundColor: showIsTrueOnly ? "#4CAF50" : "#f0f0f0",
            color: showIsTrueOnly ? "white" : "black",
          }}
        >
          Show Is True Only
        </button>
        <button
          onClick={() => setShowPriceOnly(!showPriceOnly)}
          className={`button ${showPriceOnly ? "active" : ""}`}
          style={{
            backgroundColor: showPriceOnly ? "#4CAF50" : "#f0f0f0",
            color: showPriceOnly ? "white" : "black",
          }}
        >
          Show Price Only
        </button>
        <button
          onClick={handleDeleteAllVisible}
          className="button"
          style={{
            backgroundColor: "#ff4d4d",
            color: "white",
            fontWeight: "bold",
            padding: "8px 15px",
          }}
          disabled={isDeleting || filteredTexts.length === 0}
        >
          {isDeleting ? "Deleting..." : `Delete Items (${filteredTexts.length})`}

        </button>
      </div>

      <table
        id="price-products-table"
        style={{ fontSize: "16px", width: "100%", fontWeight: "bold" }}
      >
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort("ID")}>
              #
            </th>
            <th className="sortable" onClick={() => handleSort("Text")}>
              Text
            </th>
            <th className="sortable" onClick={() => handleSort("IsTrue")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                }}
              >
                Is True
                <input
                  type="checkbox"
                  checked={selectAllIsTrue}
                  onChange={handleSelectAllIsTrue}
                  style={{ cursor: "pointer", width: "20px", height: "20px" }}
                />
              </div>
            </th>
            <th className="sortable" onClick={() => handleSort("Price")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                }}
              >
                Price
                <input
                  type="checkbox"
                  checked={selectAllPrice}
                  onChange={handleSelectAllPrice}
                  style={{ cursor: "pointer", width: "20px", height: "20px" }}
                />
              </div>
            </th>
            <th className="sortable" onClick={() => handleSort("LastUpdated")}>
              Last Updated
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6">Loading...</td>
            </tr>
          ) : filteredTexts.length === 0 ? (
            <tr>
              <td colSpan="6">No texts found</td>
            </tr>
          ) : (
            filteredTexts.map((text, index) => (
              <tr key={text.ID}>
                <td>{index + 1}</td>
                <td>
                  {editingId === text.ID ? (
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      style={{ width: "100%", padding: "4px" }}
                    />
                  ) : (
                    text.Text
                  )}
                </td>
                <td style={{ textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={text.IsTrue}
                    onChange={() => handleToggleIsTrue(text)}
                    style={{ cursor: "pointer", width: "20px", height: "20px" }}
                  />
                </td>
                <td style={{ textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={text.Price}
                    onChange={() => handleTogglePrice(text)}
                    style={{ cursor: "pointer", width: "20px", height: "20px" }}
                  />
                </td>
                <td style={{ textAlign: "center" }}>
                  {formatDate(text.LastUpdated)}
                </td>
                <td>
                  {editingId === text.ID ? (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() =>
                          handleUpdate(text.ID, {
                            text: editText,
                            isTrue: text.IsTrue,
                            price: editPrice,
                          })
                        }
                        className="button"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="button"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => handleEdit(text)}
                        className="button"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(text.ID)}
                        className="button"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <footer>
        <span>© 2025 OwlTech All rights reserved.</span>
      </footer>
    </div>
  );
};

export default TextsManagement;